/**
 * Where you write to the assistant. Enter sends and Shift+Enter starts a new line (on touch,
 * Return is a new line and the button sends; Ctrl+Enter sends everywhere). The box grows with
 * what you write. While a reply runs you keep typing: Enter holds the message until the reply
 * finishes (the queue above the box, where each one can be edited or dropped), and Stop (or
 * Esc) ends the reply. Up in an empty box edits your last message. The microphone writes what
 * you say where the browser can. Errors show under the box, never in a toast.
 */
import { ArrowUp, Check, ListPlus, Mic, Pencil, Square, X } from '@orgus/ui/icons';
import { forwardRef, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { ASSISTANT_MESSAGE_MAX } from '@orgus/protocol';
import { Button, IconButton, Kbd, Textarea, cn, formatInt, hasMod, toUiError } from '@orgus/ui';
import type { QueuedMessage } from './composerState.ts';
import { messageError } from './model.ts';

export interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  /** Send the text; reject to keep it in the box and show why. */
  onSend: (text: string) => Promise<void>;
  /** A reply is running: Enter holds the message and Stop ends the reply. */
  busy: boolean;
  onStop?: () => Promise<void>;
  /** Hold a message until the reply finishes. */
  onQueue?: (text: string) => void;
  queued?: QueuedMessage[];
  onEditQueued?: (id: string, text: string) => void;
  onRemoveQueued?: (id: string) => void;
  /** Up in an empty box: your last message, to edit and send again. */
  lastMessage?: string | null;
  /** Why nothing can be sent right now (the assistant is off), shown instead of the buttons. */
  blocked?: string | null;
  placeholder?: string;
}

const COUNT_FROM = 0.8;

const coarse = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

interface Recognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start: () => void;
  stop: () => void;
}

function recognitionCtor(): (new () => Recognition) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** A message waiting to send, which can be edited or dropped. */
function QueuedRow({ m, onEdit, onRemove }: { m: QueuedMessage; onEdit?: (id: string, text: string) => void; onRemove?: (id: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(m.text);
  return (
    <li className="flex items-start gap-2 rounded-card border border-accent-line bg-accent-soft px-3 py-2 text-caption">
      {editing ? (
        <>
          <Textarea
            aria-label="Edit the waiting message"
            rows={2}
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onEdit?.(m.id, text);
                setEditing(false);
              } else if (e.key === 'Escape') {
                setText(m.text);
                setEditing(false);
              }
            }}
            wrapperClassName="min-w-0 flex-1"
          />
          <IconButton size="xs" label="Save this message" icon={<Check />} onClick={() => (onEdit?.(m.id, text), setEditing(false))} />
        </>
      ) : (
        <>
          {/* On a phone the label takes its own line so the message keeps the width. */}
          <div className="flex min-w-0 flex-1 flex-col sm:flex-row sm:gap-2">
            <span className="shrink-0 text-fg-subtle">Waiting for the reply to finish:</span>
            <span className="min-w-0 flex-1 truncate text-fg">{m.text}</span>
          </div>
          <IconButton size="xs" label="Edit this message" icon={<Pencil />} onClick={() => setEditing(true)} />
        </>
      )}
      <IconButton size="xs" label="Drop this message" icon={<X />} onClick={() => onRemove?.(m.id)} />
    </li>
  );
}

export const Composer = forwardRef<HTMLTextAreaElement, ComposerProps>(function Composer(
  { value, onChange, onSend, busy, onStop, onQueue, queued = [], onEditQueued, onRemoveQueued, lastMessage, blocked, placeholder },
  ref,
) {
  const [sending, setSending] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [listening, setListening] = useState(false);
  const rec = useRef<Recognition | null>(null);
  const base = useRef('');
  const Ctor = recognitionCtor();

  useEffect(() => () => rec.current?.stop(), []);

  const send = async () => {
    const invalid = messageError(value);
    if (invalid) return setError(invalid);
    if (busy) {
      if (!onQueue) return;
      onQueue(value.trim());
      onChange('');
      setError(null);
      return;
    }
    setSending(true);
    setError(null);
    try {
      await onSend(value.trim());
    } catch (e) {
      const u = toUiError(e);
      setError([u.what, u.why, u.next].filter(Boolean).join(' '));
    } finally {
      setSending(false);
    }
  };

  const stop = async () => {
    if (!onStop) return;
    setStopping(true);
    setError(null);
    try {
      await onStop();
    } catch (e) {
      const u = toUiError(e);
      setError([u.what, u.why, u.next].filter(Boolean).join(' '));
    } finally {
      setStopping(false);
    }
  };

  const listen = () => {
    if (!Ctor) return;
    if (listening) {
      rec.current?.stop();
      return;
    }
    const r = new Ctor();
    r.continuous = true;
    r.interimResults = true;
    r.lang = navigator.language || 'en-US';
    base.current = value ? `${value.replace(/\s+$/, '')} ` : '';
    r.onresult = (e) => {
      let text = '';
      for (let i = 0; i < e.results.length; i++) text += e.results[i]![0]!.transcript;
      onChange(`${base.current}${text}`);
    };
    r.onerror = (e) => {
      if (e.error === 'not-allowed') setError('The microphone is blocked for this page. Allow it in the browser’s site settings, then try again.');
      else if (e.error !== 'no-speech' && e.error !== 'aborted') setError(`Dictation stopped: ${e.error.replace(/-/g, ' ')}. Type the message instead, or try again.`);
    };
    r.onend = () => {
      setListening(false);
      rec.current = null;
    };
    rec.current = r;
    setError(null);
    r.start();
    setListening(true);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && (hasMod(e) || (!e.shiftKey && !coarse()))) {
      e.preventDefault();
      if (!blocked && !sending) void send();
      return;
    }
    if (e.key === 'Escape' && busy && onStop && !e.repeat) {
      e.preventDefault();
      void stop();
      return;
    }
    if (e.key === 'ArrowUp' && !value && lastMessage && !busy && e.currentTarget.selectionStart === 0) {
      e.preventDefault();
      onChange(lastMessage);
    }
  };

  const count = value.length;
  const showCount = count >= ASSISTANT_MESSAGE_MAX * COUNT_FROM;
  const over = count > ASSISTANT_MESSAGE_MAX;

  return (
    <div className="flex flex-col gap-2">
      {queued.length > 0 && (
        <ul className="flex flex-col gap-1" aria-label={queued.length === 1 ? 'One message waiting to send' : `${queued.length} messages waiting to send`}>
          {queued.map((m) => (
            <QueuedRow key={m.id} m={m} onEdit={onEditQueued} onRemove={onRemoveQueued} />
          ))}
        </ul>
      )}
      <div
        className={cn(
          'flex flex-col gap-1 rounded-overlay border bg-surface px-4 pb-2 pt-3 shadow-elevation-1 transition-colors duration-(--duration-fast)',
          focused ? 'border-border-strong' : 'border-border',
          listening && 'border-accent',
          blocked && 'opacity-60',
        )}
      >
        <Textarea
          ref={ref}
          aria-label="Message to the assistant"
          rows={1}
          value={value}
          placeholder={placeholder ?? (busy ? 'Type your next message' : 'Message the assistant')}
          onChange={(e) => {
            onChange(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={!!blocked}
          enterKeyHint={coarse() ? 'enter' : 'send'}
          wrapperClassName="min-w-0"
          className="field-sizing-content max-h-48 min-h-12 resize-none border-0 bg-transparent px-0 py-1 text-body shadow-none focus-visible:ring-0"
        />
        <div className="flex min-h-control items-center gap-1">
          <span className="min-w-0 flex-1 truncate text-caption">
            {error ? (
              <span role="alert" className="text-status-danger-ink">
                {error}
              </span>
            ) : blocked ? (
              <span className="text-fg-muted">{blocked}</span>
            ) : showCount ? (
              <span className={cn('num', over ? 'text-status-danger-ink' : 'text-fg-subtle')}>
                {formatInt(count)} / {formatInt(ASSISTANT_MESSAGE_MAX)}
                {over ? `, ${formatInt(count - ASSISTANT_MESSAGE_MAX)} over` : ''}
              </span>
            ) : focused && !coarse() ? (
              <span className="hidden items-center gap-1 text-fg-subtle sm:flex">
                <Kbd keys={['shift', 'enter']} /> new line
                {lastMessage && !busy && !value && (
                  <>
                    <span className="px-1" aria-hidden />
                    <Kbd keys={['up']} /> edit your last message
                  </>
                )}
              </span>
            ) : null}
          </span>
          {Ctor && !blocked && <IconButton size="sm" label={listening ? 'Stop dictating' : 'Dictate a message'} icon={<Mic />} pressed={listening} onClick={listen} />}
          {busy && value.trim() && onQueue && !blocked && <IconButton size="sm" label="Send when the reply finishes" icon={<ListPlus />} onClick={() => void send()} />}
          {busy ? (
            <Button variant="secondary" size="sm" icon={<Square className="fill-current" />} loading={stopping} disabled={!onStop} onClick={() => void stop()} shortcut={['esc']}>
              Stop reply
            </Button>
          ) : (
            <Button variant="primary" size="sm" icon={<ArrowUp />} loading={sending} disabled={!!blocked || !value.trim() || over} onClick={() => void send()}>
              Send
            </Button>
          )}
        </div>
      </div>
      {over && <span className="sr-only">Shorten this message to send it.</span>}
    </div>
  );
});

